import { parseTeamPath } from './parseTeamPath';

export interface StoryPointsData {
  total: number;
  computed: number;
  totalTasks: number;
  completedStoryPoints?: number;
  completedStoryPointsComputed?: number;
  perTeam: Record<string, {
    total: number;
    computed: number;
    taskCount: number;
    teamName: string;
    projectName: string;
    sprintName: string;
    taskBreakdown: Record<string, number>;
    statusBreakdown: Record<string, number>;
    completedStoryPoints?: number;
    completedStoryPointsComputed?: number;
  }>;
  taskBreakdown?: Record<string, number>;
  statusBreakdown?: Record<string, number>;
}

/**
 * Retrieves and processes story points data for a specific resource.
 *
 * @param resourceId - The ID of the resource
 * @param capacityPlanningData - The capacity planning data from the API
 * @param searchType - The current search filter type ('team' or other)
 * @param teamFilterState - The team filter state containing current sprint info
 * @returns Processed story points data or null if not available
 */
export const getStoryPointsForResource = (
  resourceId: string,
  capacityPlanningData: any,
  searchType: string,
  teamFilterState: any
): StoryPointsData | null => {
  if (!capacityPlanningData?.allocated_task) return null;

  const resourceData = capacityPlanningData.allocated_task[resourceId];
  if (!resourceData?.stats) return null;

  const isTeamFilter = searchType === 'team';
  const stats = resourceData.stats;
  const storyPointsPerTeam = stats.story_points_per_team || {};
  const statusBreakdownPerTeam = stats.status_breakdown_per_team || {};
  const taskBreakdownPerTeam = stats.task_breakdown_per_team || {};

  // Extract breakdowns
  const taskBreakdown = stats.task_breakdown || {};
  const statusBreakdown = stats.overall_status_breakdown || {};

  // Calculate total tasks from status breakdown or task breakdown
  const totalTasks = statusBreakdown.total ||
    (Object.values(taskBreakdown) as number[]).reduce((a: number, b: number) => a + b, 0) || 0;

  if (isTeamFilter && teamFilterState?.current_sprint) {
    // For team filter, show story points for ALL teams this resource is assigned to
    // This gives complete visibility of the resource's allocation across teams
    const parsedPerTeam: Record<string, {
      total: number;
      computed: number;
      taskCount: number;
      teamName: string;
      projectName: string;
      sprintName: string;
      taskBreakdown: Record<string, number>;
      statusBreakdown: Record<string, number>;
      completedStoryPoints?: number;
      completedStoryPointsComputed?: number;
    }> = {};

    let totalPoints = 0;
    let computedPoints = 0;
    let completedStoryPointsTotal = 0;
    let completedStoryPointsComputedTotal = 0;

    // Process ALL team entries for this resource
    Object.entries(storyPointsPerTeam).forEach(([key, value]) => {
      const val = value as any;
      let teamTotal = 0;
      let teamComputed = 0;

      if (val) {
        teamTotal = val.total;
        teamComputed = val.computed;
      }

      const teamCompletedSP = val?.completed || 0;
      const teamCompletedSPComputed = val?.completed_computed || 0;

      totalPoints += teamTotal;
      computedPoints += teamComputed;
      completedStoryPointsTotal += teamCompletedSP;
      completedStoryPointsComputedTotal += teamCompletedSPComputed;

      // Get task count for this team
      const teamStats = statusBreakdownPerTeam[key] || {};
      const teamTaskCount = teamStats.total || 0;

      // Get task breakdown for this team
      const teamTaskBreakdown = taskBreakdownPerTeam[key] || {};

      // Get status breakdown for this team (exclude total)
      const teamStatusBreakdown = { ...teamStats };
      delete teamStatusBreakdown.total;

      parsedPerTeam[key] = {
        total: teamTotal || 0,
        computed: teamComputed || 0,
        taskCount: teamTaskCount,
        taskBreakdown: teamTaskBreakdown,
        statusBreakdown: teamStatusBreakdown,
        completedStoryPoints: val?.completed || 0,
        completedStoryPointsComputed: val?.completed_computed || 0,
        ...parseTeamPath(key)
      };
    });

    return {
      total: totalPoints,
      computed: computedPoints,
      totalTasks,
      completedStoryPoints: completedStoryPointsTotal,
      completedStoryPointsComputed: completedStoryPointsComputedTotal,
      perTeam: parsedPerTeam,
      taskBreakdown,
      statusBreakdown
    };
  } else {
    // For non-team filter, show total story points with parsed team info
    const parsedPerTeam: Record<string, {
      total: number;
      computed: number;
      taskCount: number;
      teamName: string;
      projectName: string;
      sprintName: string;
      taskBreakdown: Record<string, number>;
      statusBreakdown: Record<string, number>;
      completedStoryPoints?: number;
      completedStoryPointsComputed?: number;
    }> = {};

    Object.entries(storyPointsPerTeam).forEach(([key, value]) => {
      const val = value as any;
      let total = 0;
      let computed = 0;

      if (val) {
        total = val.total;
        computed = val.computed;
      }

      // Get task count for this team
      const teamStats = statusBreakdownPerTeam[key] || {};
      const teamTaskCount = teamStats.total || 0;

      // Get task breakdown for this team
      const teamTaskBreakdown = taskBreakdownPerTeam[key] || {};

      // Get status breakdown for this team (exclude total)
      const teamStatusBreakdown = { ...teamStats };
      delete teamStatusBreakdown.total;

      parsedPerTeam[key] = {
        total: total || 0,
        computed: computed || 0,
        taskCount: teamTaskCount,
        taskBreakdown: teamTaskBreakdown,
        statusBreakdown: teamStatusBreakdown,
        completedStoryPoints: val?.completed || 0,
        completedStoryPointsComputed: val?.completed_computed || 0,
        ...parseTeamPath(key)
      };
    });

    return {
      total: stats.total_story_points || 0,
      computed: stats.story_points_computed_by_cadence || 0,
      totalTasks,
      completedStoryPoints: stats.total_completed_story_points || 0,
      completedStoryPointsComputed: stats.completed_story_points_computed_by_cadence || 0,
      perTeam: parsedPerTeam,
      taskBreakdown,
      statusBreakdown
    };
  }
};

export interface OriginalEstimateData {
  total: number;
  computed: number;
  completed: number;
  completedComputed?: number;
  perTeam: Record<string, {
    total: number;
    computed: number;
    completed: number;
    completedComputed?: number;
    teamName: string;
    projectName: string;
    sprintName: string;
  }>;
}

/**
 * Retrieves and processes original estimate data for a specific resource.
 * Original estimates are typically used for task types (as opposed to story points for user stories).
 *
 * @param resourceId - The ID of the resource
 * @param capacityPlanningData - The capacity planning data from the API
 * @param searchType - The current search filter type ('team' or other)
 * @param teamFilterState - The team filter state containing current sprint info
 * @returns Processed original estimate data or null if not available
 */
export const getOriginalEstimateForResource = (
  resourceId: string,
  capacityPlanningData: any,
  searchType: string,
  teamFilterState: any
): OriginalEstimateData | null => {
  if (!capacityPlanningData?.allocated_task) return null;

  const resourceData = capacityPlanningData.allocated_task[resourceId];
  if (!resourceData?.stats) return null;

  const stats = resourceData.stats;
  const originalEstimatePerTeam = stats.original_estimate_per_team || {};

  const isTeamFilter = searchType === 'team';

  if (isTeamFilter && teamFilterState?.current_sprint) {
    // For team filter, show original estimates for ALL teams this resource is assigned to
    const parsedPerTeam: Record<string, {
      total: number;
      computed: number;
      completed: number;
      completedComputed?: number;
      teamName: string;
      projectName: string;
      sprintName: string;
    }> = {};

    let totalEstimate = 0;
    let computedEstimate = 0;
    let completedEstimate = 0;
    let completedComputedEstimate = 0;

    // Process ALL team entries for this resource
    Object.entries(originalEstimatePerTeam).forEach(([key, value]) => {
      const val = value as any;
      let teamTotal = 0;
      let teamComputed = 0;
      let teamCompleted = 0;

      if (val) {
        teamTotal = val.total || 0;
        teamComputed = val.computed || 0;
        teamCompleted = val.completed || 0;
      }

      const teamCompletedComputed = val?.completed_computed || 0;

      totalEstimate += teamTotal;
      computedEstimate += teamComputed;
      completedEstimate += teamCompleted;
      completedComputedEstimate += teamCompletedComputed;

      parsedPerTeam[key] = {
        total: teamTotal,
        computed: teamComputed,
        completed: teamCompleted,
        completedComputed: val?.completed_computed || 0,
        ...parseTeamPath(key)
      };
    });

    return {
      total: totalEstimate,
      computed: computedEstimate,
      completed: completedEstimate,
      completedComputed: completedComputedEstimate,
      perTeam: parsedPerTeam
    };
  } else {
    // For non-team filter, show total original estimates with parsed team info
    const parsedPerTeam: Record<string, {
      total: number;
      computed: number;
      completed: number;
      completedComputed?: number;
      teamName: string;
      projectName: string;
      sprintName: string;
    }> = {};

    Object.entries(originalEstimatePerTeam).forEach(([key, value]) => {
      const val = value as any;
      let total = 0;
      let computed = 0;
      let completed = 0;

      if (val) {
        total = val.total || 0;
        computed = val.computed || 0;
        completed = val.completed || 0;
      }

      parsedPerTeam[key] = {
        total,
        computed,
        completed,
        completedComputed: val?.completed_computed || 0,
        ...parseTeamPath(key)
      };
    });

    return {
      total: stats.total_original_estimate || 0,
      computed: stats.original_estimate_computed_by_cadence || 0,
      completed: stats.total_completed_original_estimate || 0,
      completedComputed: stats.completed_original_estimate_computed_by_cadence || 0,
      perTeam: parsedPerTeam
    };
  }
};